/**
 * streamChatResponse - Utility for streaming ChatGPT responses via SSE
 * 
 * Fetches streaming response from the backend and calls onChunk for each
 * text fragment received. Does NOT auto-scroll - leaves that to consumer.
 * 
 * On native platforms (Android/iOS), streaming via fetch is blocked by CORS
 * (since CapacitorHttp is disabled and we can't use it for streaming anyway).
 * So on native, we use the non-streaming endpoint with typewriter simulation.
 * 
 * @author Heseda AI Team
 * @version 1.2
 */

import { getAuthHeaders } from '../services/apiAuth';
import { getApiBaseUrl, isNativePlatform, nativeFetch } from './apiConfig';
import { sanitizeChatMessages } from './messageSanitizer';

// Constants for buffering detection
// These help detect when a streaming response was buffered by network/proxy
const MAX_BUFFERED_CHUNKS = 3;      // Max chunks that still indicate buffering
const MIN_CONTENT_EVENTS = 5;       // Min content events to consider buffering
const MIN_BUFFERED_LENGTH = 100;    // Min text length to trigger typewriter simulation

// Typewriter speed constants (chars per second)
const WEB_TYPEWRITER_SPEED = 100;   // Slightly slower for web fallback
const NATIVE_TYPEWRITER_SPEED = 80; // Faster for native platforms

export interface StreamChatOptions {
  prompt: string;
  sessionId: string;
  messages: Array<{ sender: string; text: string; type?: string; webSearchResults?: any[] }>;
  language: string;
  profileCompletion: number;
  masterProfile: any;
  searchContext?: any;
  editorPicksContext?: {
    picks: Array<{
      title: string;
      brand?: string;
      price?: number;
      score?: number;
      reason?: string;
      affiliateSource?: string;
    }>;
    isAffiliate?: boolean;
  };
  timezone?: string;
  isCompact?: boolean;
  openAIConversationId?: string;
  isImageAnalysis?: boolean;
  searchRemaining?: number | null;
}

export interface StreamResult {
  fullText: string;
  triggerSearch: boolean;
  openAIConversationId?: string;
}

// Re-export for backward compat with tests
export { sanitizeChatMessages as sanitizeMessages } from './messageSanitizer';

/**
 * Simulate typewriter effect for text that arrived all at once
 * This is used when the backend doesn't support true streaming
 */
async function simulateTypewriter(
  text: string, 
  onChunk: (text: string) => void,
  charsPerSecond: number = 60
): Promise<void> {
  const delay = 1000 / charsPerSecond;
  let displayed = '';
  
  // Process by small word chunks for more natural feel
  const words = text.split(/(\s+)/); // Split by whitespace but keep delimiters
  
  for (const word of words) {
    displayed += word;
    onChunk(displayed);
    // Vary delay slightly for natural feel
    await new Promise(resolve => setTimeout(resolve, delay * word.length * (0.5 + Math.random() * 0.5)));
  }
}

/**
 * Stream chat response from OpenAI via SSE
 * @param options - Request options
 * @param onChunk - Callback for each text chunk received (for progressive UI updates)
 * @returns Final result with full text and metadata
 */
export async function streamChatResponse(
  options: StreamChatOptions,
  onChunk: (text: string) => void
): Promise<StreamResult> {
  const authHeaders = await getAuthHeaders();
  
  // On native platforms, use non-streaming endpoint with typewriter effect
  // because regular fetch is blocked by CORS and CapacitorHttp doesn't support streaming
  if (isNativePlatform()) {
    return streamChatResponseNative(options, onChunk, authHeaders);
  }
  
  // Web: use actual streaming
  const apiUrl = `${getApiBaseUrl()}/getOpenAIResponseStream`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt: options.prompt,
      sessionId: options.sessionId,
      messages: sanitizeChatMessages(options.messages),
      language: options.language,
      profileCompletion: options.profileCompletion,
      masterProfile: options.masterProfile,
      searchContext: options.searchContext,
      editorPicksContext: options.editorPicksContext,
      timezone: options.timezone,
      isCompact: options.isCompact,
      openAIConversationId: options.openAIConversationId,
      isImageAnalysis: options.isImageAnalysis,
      searchRemaining: options.searchRemaining,
    }),
  });

  if (!response.ok) {
    // Try to get error details from response
    let errorMessage = `Stream request failed: ${response.status}`;
    let errorData: Record<string, unknown> | null = null;
    try {
      errorData = await response.json();
      errorMessage = (errorData?.error as string) || errorMessage;
    } catch {
      // Ignore JSON parse error
    }
    // Preserve HTTP status + response data so callers can detect 429 / retryAfter
    const err = new Error(errorMessage) as Error & { status?: number; responseData?: Record<string, unknown> };
    err.status = response.status;
    err.responseData = errorData ?? undefined;
    throw err;
  }

  // Reject HTML responses (SPA fallback when API endpoint is unreachable)
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    throw new Error('API returned HTML instead of SSE — endpoint may be unreachable');
  }

  if (!response.body) {
    throw new Error('No response body for streaming');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let triggerSearch = false;
  let openAIConversationId: string | undefined;
  let buffer = '';
  let chunkCount = 0;
  let totalContentEvents = 0;
  let firstChunkTime = 0;
  let lastChunkTime = 0;

  while (true) {
    const { done, value } = await reader.read();
    
    if (done) break;
    
    chunkCount++;
    const now = Date.now();
    if (chunkCount === 1) firstChunkTime = now;
    lastChunkTime = now;
    
    // Decode the chunk and add to buffer
    buffer += decoder.decode(value, { stream: true });
    
    // Process complete SSE lines from buffer
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer
    
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const jsonStr = line.slice(6).trim();
        if (!jsonStr) continue;
        
        try {
          const data = JSON.parse(jsonStr);
          
          if (data.content) {
            fullText += data.content;
            totalContentEvents++;
            // Send progressive updates to UI
            onChunk(fullText);
          }
          
          if (data.done) {
            triggerSearch = data.triggerSearch || false;
            openAIConversationId = data.openAIConversationId;
            // Log model version for debugging (visible in F12 console)
            if (data.model) {
              console.log(`🤖 AI Model used: ${data.model}`);
            }
          }
          
          if (data.error) {
            console.error('Stream error:', data.error);
          }
        } catch (e) {
          console.warn('Failed to parse SSE data:', jsonStr);
        }
      }
    }
  }
  
  // Detect if response was buffered (came all at once)
  // This can happen with some network configurations or proxies
  const totalTime = lastChunkTime - firstChunkTime;
  const wasBuffered = (
    // Very few chunks with lots of content = buffered
    chunkCount <= MAX_BUFFERED_CHUNKS && 
    totalContentEvents > MIN_CONTENT_EVENTS && 
    fullText.length > MIN_BUFFERED_LENGTH
  );
  
  if (wasBuffered) {
    console.log('Response was buffered, simulating typewriter effect');
    // Clear the current text first for clean typewriter effect
    onChunk('');
    await simulateTypewriter(fullText, onChunk);
  }

  return { fullText, triggerSearch, openAIConversationId };
}

/**
 * Native platform implementation using non-streaming endpoint with typewriter effect.
 * Uses CapacitorHttp via nativeFetch() to bypass CORS.
 */
async function streamChatResponseNative(
  options: StreamChatOptions,
  onChunk: (text: string) => void,
  authHeaders: Record<string, string>
): Promise<StreamResult> {
  console.log('📱 Using non-streaming API with typewriter effect for native');
  
  // Use the non-streaming endpoint which works with CapacitorHttp
  const apiUrl = `${getApiBaseUrl()}/getOpenAIResponse`;
  
  const response = await nativeFetch(apiUrl, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt: options.prompt,
      sessionId: options.sessionId,
      messages: sanitizeChatMessages(options.messages),
      language: options.language,
      profileCompletion: options.profileCompletion,
      masterProfile: options.masterProfile,
      searchContext: options.searchContext,
      editorPicksContext: options.editorPicksContext,
      timezone: options.timezone,
      isCompact: options.isCompact,
      openAIConversationId: options.openAIConversationId,
      isImageAnalysis: options.isImageAnalysis,
      searchRemaining: options.searchRemaining,
    }),
  });

  if (!response.ok) {
    let errorMessage = `API request failed: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData?.error || errorMessage;
    } catch {
      // Ignore JSON parse error
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();

  // Guard: reject HTML responses (SPA fallback when API endpoint is unreachable)
  if (typeof data === 'string' && data.trimStart().startsWith('<!DOCTYPE')) {
    throw new Error('API returned HTML instead of JSON — endpoint may be unreachable');
  }
  
  // Extract response text
  const fullText = typeof data === 'object' 
    ? (data.response || data.message || JSON.stringify(data))
    : String(data);
  
  const triggerSearch = typeof data === 'object' && data.triggerSearch;
  const openAIConversationId = typeof data === 'object' ? data.openAIConversationId : undefined;
  
  // Log model version
  if (data.model) {
    console.log(`🤖 AI Model used: ${data.model}`);
  }
  
  // Simulate typewriter effect for better UX
  await simulateTypewriter(fullText, onChunk, NATIVE_TYPEWRITER_SPEED);
  
  return { fullText, triggerSearch, openAIConversationId };
}
